/**
 * AccountSelectionModal Component
 * Cari hesap seçimi için modal component
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  InputAdornment,
  CircularProgress,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import { Account } from '../types/invoice.types';

interface AccountSelectionModalProps {
  open: boolean;
  onClose: () => void;
  accounts: Account[];
  searchTerm: string;
  onSearchChange: (value: string) => void;
  page: number;
  rowsPerPage: number;
  onPageChange: (event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => void;
  onRowsPerPageChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onSelectAccount: (account: Account) => void;
  loading?: boolean;
}

export const AccountSelectionModal: React.FC<AccountSelectionModalProps> = ({
  open,
  onClose,
  accounts,
  searchTerm,
  onSearchChange,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onSelectAccount,
  loading = false,
}) => {
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (trText: string, enText: string) => (isTr ? trText : enText);

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{tt('Cari Hesap Seç', 'Select Account')}</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          placeholder={tt('Cari hesap ara...', 'Search account...')}
          value={searchTerm}
          onChange={(e) => onSearchChange(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{tt('Cari Adı', 'Account Name')}</TableCell>
                    <TableCell>{tt('Vergi No', 'Tax ID')}</TableCell>
                    <TableCell>{tt('Telefon', 'Phone')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="text.secondary">
                          {searchTerm ? tt('Sonuç bulunamadı', 'No results found') : tt('Cari hesap bulunamadı', 'No account found')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    accounts
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((account) => (
                        <TableRow
                          key={account.id}
                          hover
                          onClick={() => onSelectAccount(account)}
                          sx={{ cursor: 'pointer' }}
                        >
                          <TableCell>{account.companyName}</TableCell>
                          <TableCell>{account.taxNumber || '-'}</TableCell>
                          <TableCell>{account.phone || '-'}</TableCell>
                        </TableRow>
                      ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {accounts.length > 0 && (
              <TablePagination
                component="div"
                count={accounts.length}
                page={page}
                onPageChange={onPageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 25]}
                labelRowsPerPage={tt('Sayfa başına:', 'Rows per page:')}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
